import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Kbd,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@exiftrator/shared/components/ui";
import {
  GLOBAL_SHORTCUTS,
  SHORTCUT_VIEW_IDS,
  SHORTCUT_VIEW_LABELS,
  VIEW_SHORTCUTS,
} from "./keyboardShortcutsCatalog";

const DEFAULT_VIEW = "editor";
const GLOBAL_TAB_ID = "global";
const SHORTCUT_TAB_IDS = [GLOBAL_TAB_ID, ...SHORTCUT_VIEW_IDS];
const SHORTCUT_TAB_LABELS = {
  [GLOBAL_TAB_ID]: "Global",
  ...SHORTCUT_VIEW_LABELS,
};

function resolveToken(token, isMacLike) {
  if (token === "mod") {
    return isMacLike ? "⌘" : "Ctrl";
  }
  if (token === "ctrl") {
    return "Ctrl";
  }
  if (token === "alt") {
    return isMacLike ? "⌥" : "Alt";
  }
  if (token === "Shift") {
    return "Shift";
  }
  if (token === "Escape") {
    return "Esc";
  }
  if (token === "Enter") {
    return "↵";
  }
  if (token === "Backspace") {
    return isMacLike ? "⌫" : "Backspace";
  }
  if (token === "Delete") {
    return isMacLike ? "⌦" : "Delete";
  }
  if (token === "Arrow Up") {
    return "↑";
  }
  if (token === "Arrow Down") {
    return "↓";
  }
  if (token === "Arrow Left") {
    return "←";
  }
  if (token === "Arrow Right") {
    return "→";
  }
  if (token === "Arrow Left/Right") {
    return "←/→";
  }
  if (token === "Arrow keys") {
    return "← ↑ ↓ →";
  }
  if (token === "Arrow") {
    return "← ↑ ↓ →";
  }
  return token;
}

function ShortcutCombos({
  combos,
  comboSeparator = "or",
  isMacLike,
}) {
  if (!Array.isArray(combos) || combos.length === 0) {
    return null;
  }

  const normalizedSeparator = comboSeparator === "/" ? " / " : " or ";

  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {combos.map((combo, comboIndex) => (
        <React.Fragment key={`combo-${combo.join("-")}-${comboIndex}`}>
          {combo.map((token, tokenIndex) => (
            <React.Fragment key={`${comboIndex}-${token}-${tokenIndex}`}>
              <Kbd>{resolveToken(token, isMacLike)}</Kbd>
            </React.Fragment>
          ))}
          {comboIndex < combos.length - 1 && (
            <span className="px-1 text-xs text-text-tertiary">
              {normalizedSeparator.trim()}
            </span>
          )}
        </React.Fragment>
      ))}
    </span>
  );
}

function ShortcutTable({ shortcuts, isMacLike }) {
  if (!Array.isArray(shortcuts) || shortcuts.length === 0) {
    return (
      <p className="rounded-md border border-border-subtle bg-bg-secondary/50 px-3 py-2 text-xs text-text-secondary">
        No shortcuts yet.
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-border-subtle">
      <Table>
        <TableHeader>
          <TableRow className="bg-bg-secondary/55">
            <TableHead className="w-[42%]">Keys</TableHead>
            <TableHead>Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shortcuts.map((shortcut, index) => (
            <TableRow key={`${shortcut.description}-${index}`}>
              <TableCell>
                <ShortcutCombos
                  combos={shortcut.combos}
                  comboSeparator={shortcut.comboSeparator}
                  isMacLike={isMacLike}
                />
              </TableCell>
              <TableCell className="text-sm text-text-primary">
                {shortcut.description}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function KeyboardShortcutsDialog({
  open,
  onOpenChange,
  initialView = DEFAULT_VIEW,
}) {
  const normalizedInitialView = useMemo(
    () =>
      SHORTCUT_VIEW_IDS.includes(initialView)
        ? initialView
        : DEFAULT_VIEW,
    [initialView],
  );
  const isMacLike = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const platform = navigator.platform || "";
    const ua = navigator.userAgent || "";
    return /Mac|iPhone|iPad|iPod/i.test(`${platform} ${ua}`);
  }, []);
  const [activeView, setActiveView] = useState(normalizedInitialView);

  useEffect(() => {
    if (open) {
      setActiveView(normalizedInitialView);
    }
  }, [open, normalizedInitialView]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-hidden p-0 sm:max-w-3xl">
        <div className="flex max-h-[80vh] min-h-0 flex-col">
          <DialogHeader className="shrink-0 border-b border-border-subtle px-6 pt-6 pb-4">
            <DialogTitle>Keyboard shortcuts</DialogTitle>
            <DialogDescription>
              Global and view-specific shortcuts are grouped by tab.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <Tabs value={activeView} onValueChange={setActiveView}>
              <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-5">
                {SHORTCUT_TAB_IDS.map((tabId) => (
                  <TabsTrigger key={tabId} value={tabId}>
                    {SHORTCUT_TAB_LABELS[tabId]}
                  </TabsTrigger>
                ))}
              </TabsList>

              {SHORTCUT_TAB_IDS.map((tabId) => (
                <TabsContent key={tabId} value={tabId} className="mt-4">
                  <ShortcutTable
                    shortcuts={
                      tabId === GLOBAL_TAB_ID
                        ? GLOBAL_SHORTCUTS
                        : VIEW_SHORTCUTS[tabId]
                    }
                    isMacLike={isMacLike}
                  />
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default KeyboardShortcutsDialog;
